import { useQuery } from '@hooks/reactQuery';

import { getDashboardRentsSummary } from '@api/dashboardPO';

import { TPartialSearchValue } from '@interfaces/common/defaultDTO';
import { TSortedDataParams } from '@interfaces/common/response';
import { ISort } from '@interfaces/common/store';
import { IDashboardRentGeneralDTO } from '@interfaces/platform/dto/platformDashboardDTO';
import { TDashboardRentSummary } from '@interfaces/platform/platformDashboard';

import { refetchOptions } from '@constants/common';
import { DASHBOARD_RENTS_SUMMARY } from '@constants/queryKeys';

const getSortedData = ({
	data,
	sortDirection,
	sortFieldName,
	searchValue,
}: TSortedDataParams<TDashboardRentSummary> & TPartialSearchValue) => {
	if (!data) return [];

	const filteredData = data?.filter(item => {
		if (!item.organization.name) return false;

		if (!searchValue) return true;

		return item.organization.name.toLowerCase().includes(searchValue.toLowerCase());
	});

	if (!filteredData) return [];

	if (!sortFieldName) return filteredData;

	return [...filteredData].sort((a, b) => {
		const [first, second] = sortDirection ? [a, b] : [b, a];

		switch (sortFieldName) {
			case 'organization':
				return first.organization.name.localeCompare(second.organization.name);
			case 'completedRequest':
				return first.completedRequest - second.completedRequest;
			case 'requestedAssets':
				return first.requestedAssets - second.requestedAssets;
			case 'arrangedAssets':
				return first.arrangedAssets - second.arrangedAssets;
			case 'arrangedTotalCost':
				return first.arrangedTotalCost - second.arrangedTotalCost;
			case 'poEarned':
				return first.poEarned - second.poEarned;
			default:
				return 0;
		}
	}).filter(item => !!item);
};

type TUseQueryDashboardRentsSummary = IDashboardRentGeneralDTO & TPartialSearchValue & ISort

const useQueryDashboardRentsSummary = (params: TUseQueryDashboardRentsSummary) => {
	const {
		fromText,
		toText,
		sortDirection,
		sortFieldName,
		searchValue,
	} = params;

	const {
		data,
		isLoading,
		isFetching,
	} = useQuery({
		queryKey: [DASHBOARD_RENTS_SUMMARY, fromText, toText],
		queryFn: () => getDashboardRentsSummary(params),
		enabled: [!!fromText, !!toText],
		...refetchOptions,
	});

	const dashboardRentsSummary = getSortedData({
		data,
		sortDirection,
		sortFieldName,
		searchValue,
	});

	return {
		dashboardRentsSummary,
		isLoadingDashboardRentsSummary: (isLoading && !!data) || isFetching,
	};
};

export default useQueryDashboardRentsSummary;

